'use client'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { use, useEffect, useState } from 'react'
import type { Header } from '@/payload-types'
import { Logo } from '@/components/Logo/Logo'
import { HeaderNav } from './Nav'
import { Sidebar } from '@/app/(frontend)/components/Common/Sidebar'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { HiOutlineBars2 } from 'react-icons/hi2'
import { NavItem } from './getHeader'

interface HeaderClientProps {
  data: NavItem[]
}

export const HeaderClient = ({ data, locale }: any) => {
  const [theme, setTheme] = useState<string | null>(null)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()
  const isQuotePage = pathname === '/get-a-quote'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { language, setLanguage } = useLanguage()
  const isArabic = language === 'ar'
  const router = useRouter()

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setHeaderTheme(null)
  }, [pathname])

  const switchToArabic = () => {
    setLanguage('ar')
  }
  const switchToEnglish = () => {
    setLanguage('en')
    router.refresh()
  }

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
  }, [headerTheme])

  return (
    <header
      className="z-20 absolute w-full bg-transparent"
    >
      <div className={`w-full ${isQuotePage ? 'bg-transparent' : 'bg-black/40'} `}>
        {/* Top Navbar */}
        <div
          className={`w-full md:py-[17px] py-2  border-b  ${isQuotePage ? 'border-black/30' : 'border-white/30'}`}
        >
          <div
            className={`${isQuotePage ? 'text-black' : 'text-white'} flex justify-between items-center nav-container`}
          >
            {isArabic ? (
              <p
                className={`md:text-[14px] tracking-wide w-fit text-[12px] ${isQuotePage ? 'font-normal' : 'font-extralight'}`}
              >
                إعادة تصور الخدمات اللوجستية. كفاءة مُصممة بدقة.
              </p>
            ) : (
              <p
                className={`md:text-[14px] tracking-wide w-fit text-[12px] ${isQuotePage ? 'font-normal' : 'font-extralight'}`}
              >
                Logistics Reimagined. Efficiency Engineered.
              </p>
            )}

            {/* Language Switcher */}
            <div className="flex w-fit gap-2 md:gap-2.5 font-poppinss md:text-[14.5px] text-[13px] font-[100]">
              <button
                type="button"
                className={`border-b-2 tracking-[0.2em] border-transparent transition ${
                  language === 'ar' ? ' underline underline-offset-2' : ''
                } ${isQuotePage && language === 'ar' ? 'font-semibold' : ''}`}
                onClick={switchToArabic}
              >
                AR
              </button>
              <button
                type="button"
                className={`border-b-2 tracking-[0.2em] border-transparent transition ${
                  language === 'en' ? 'underline underline-offset-2' : ''
                } ${isQuotePage && language === 'en' ? 'font-semibold' : ''}`}
                onClick={switchToEnglish}
              >
                EN
              </button>
            </div>
          </div>
        </div>

        {/* navbar */}
        <div className="w-full relative">
          <div className="flex justify-between items-center nav-container md:py-5 py-3 w-full">
            <Link href="/" className="flex items-center justify-start shrink-0">
              <Logo
                isArabic={isArabic}
                variant={isQuotePage ? 'dark' : 'light'}
                className="md:w-[150px] md:h-[38px] w-[120px] h-[30px] md:-ml-[5px] "
                loading="eager"
                priority="high"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-4">
              <HeaderNav data={data} />
            </div>
            <button
              onClick={() => setSidebarOpen(true)}
              className={`lg:hidden ${isQuotePage ? 'text-black' : 'text-white'}`}
              aria-label="Open menu"
            >
              <HiOutlineBars2 className="size-8" />
            </button>
          </div>
        </div>
      </div>

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} data={data} />
    </header>
  )
}
